import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { DISABLE_ADD_PROJECT_BUTTON } from '../../../../shared/featureFlags/flagNames';
import { Action, ResourceType } from '../../../../typings/permissions';
import { useDialogContext } from '../../../contexts/DialogContext';
import { useRouter } from '../../../contexts/RouterContext';
import { window } from '../../../globals';
import { isFeatureEnabled } from '../../../helpers/features/featureFlags';
import * as ProjectsV2Store from '../../../redux/projectsV2';
import useHasPermission from '../../../shared/permissions/useHasPermission';
import CallToAction from '../../CallToAction';
import { ButtonGroup, TooltipButton } from '../../generic/Button';
import Flex from '../../generic/Flex';
import { icons, IconText } from '../../generic/Icon';
import PageHeadingBreadcrumbs from '../../generic/PageHeadingBreadcrumbs';
import TeamSelectionMenu from '../../shared/teams/TeamSelectionMenu';
import UsageBadges from '../UsageBadges';

import useOrgScanning from './hooks/useOrgScanning';

import styles from './ProjectsDashboardHeader.module.less';

const orgTitleCharacterLimit = 50;

/**
 * This is the header/CTA section for the main projects dashboard.
 * It renders the organization title and the TeamSelectionMenu,
 * with the large "Add Projects" and "Run Policy Scans" buttons.
 *
 * Optionally, if the org is a per-revision subscriber, it will render the RevisionUsageBadge.
 */
const ProjectsDashboardHeader: VoidFunctionComponent = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const hasPermission = useHasPermission();
  const { onAlert, onConfirm } = useDialogContext();
  const isAddProjectButtonDisabled = isFeatureEnabled(DISABLE_ADD_PROJECT_BUTTON);

  const { isCurrentlyScanning, onOrgScanAll } = useOrgScanning();

  const canPolicyScanAll = hasPermission({
    action: Action.Edit,
    resourceType: ResourceType.ProjectAny,
  });

  const onImportProjectClicked = useCallback(() => {
    if (hasPermission({ action: Action.Create, resourceType: ResourceType.Project }, true)) {
      router.push('/projects/import');
      return;
    }

    onAlert({
      title: 'Import Project',
      body: 'Please ask an organization administrator to import a new project.',
    });
  }, [hasPermission, onAlert, router]);

  const onScanAllClicked = useCallback(() => {
    if (canPolicyScanAll) {
      onConfirm({
        title: 'Run policy scan on all projects.',
        body: 'Are you sure you want to queue policy scans for ALL projects?',
        onConfirm: () => {
          onOrgScanAll();
          onAlert({
            title: 'Running policy scan on all of your projects.',
            body: 'All of your projects are being policy scanned. Please check back in a few minutes to see any changes.',
          });
        } });
    } else {
      onAlert({
        title: 'Failed to Start Scan',
        body: 'Please ask a project manager to start a policy scan.',
      });
    }
  }, [canPolicyScanAll, onConfirm, onOrgScanAll, onAlert]);

  const displayTitle = window.user?.organization?.title ?? 'Projects';

  //  If there is no org, just display a fallback "FOSSA" breadcrumb
  const breadcrumbs = !window.user?.organization
    ? [{ text: 'Projects' }]
    : [
        {
          content: (
            <span
              className={styles.orgTitle}
              //  Only display the "title" element if the title is longer than the character limit (and will be truncated via CSS)
              title={displayTitle.length > orgTitleCharacterLimit ? displayTitle : undefined}
            >
              {window.user?.organization?.title ?? 'Projects'}
            </span>
          ),
        },
        {
          content: (
            <TeamSelectionMenu onChange={() => {
              dispatch(ProjectsV2Store.actions.clearSelection());
              dispatch(ProjectsV2Store.actions.preFetchLoading());
              dispatch(ProjectsV2Store.actions.fetchProjects());
            }} />
          ),
        },
      ];

  return (
    <CallToAction shouldHideBottomBorder>
      <PageHeadingBreadcrumbs crumbs={breadcrumbs} />
      <div className={styles.buttons}>
        <Flex justify="space-between">
          <ButtonGroup>
            <TooltipButton
              id="btn-import-bulk"
              icon={icons.plusCircle}
              size="large"
              type="primary"
              onClick={onImportProjectClicked}
              isDisabled={isAddProjectButtonDisabled}
              showTooltip={isAddProjectButtonDisabled}
              tooltip="Disabled by your admins"
            >
              Add Projects
            </TooltipButton>
            <TooltipButton
              id="btn-bulk-rescan"
              type="outline-light"
              placement="right"
              size="large"
              isDisabled={isCurrentlyScanning || !canPolicyScanAll}
              showTooltip={!canPolicyScanAll}
              onClick={onScanAllClicked}
              tooltip="You do not have permission."
          >
              <IconText
                marginAmount="8px"
                icon={isCurrentlyScanning ? icons.circleO : icons.refresh}
                hasSpin={isCurrentlyScanning}
                text={isCurrentlyScanning ? 'Running Policy Scans' : 'Run Policy Scans'}
            />
            </TooltipButton>
          </ButtonGroup>
          <UsageBadges />
        </Flex>
      </div>
    </CallToAction>
  );
};

export default ProjectsDashboardHeader;
